import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Play, History } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import { HlsPlayer } from '@/components/seller/videos/HlsPlayer';
import { fmtKst } from '@/components/seller-profile/SellerBroadcastSchedule';

// CONSUMER-REPLAY-SELLER-PROFILE-SCHEDULE-1 · 소비자 지난 방송 다시보기 · 실시간 채팅·시청자 수·댓글 주문 없음 · 클릭 시에만 재생.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (/^https?:\/\//.test(u) ? u : `${BACKEND}${u}`);
const won = (n) => (n || n === 0 ? `${Number(n).toLocaleString('ko-KR')}원` : '');

const ReplayProduct = ({ p }) => (
  <Link to={`/product/${p.product_id}`} className="flex gap-3 p-2 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50 transition-colors" data-testid={`replay-product-${p.product_id}`}>
    <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/5 shrink-0">{p.image && <SafeImg src={p.image} kind="product" alt="" className="w-full h-full object-cover" />}</div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-bold line-clamp-2 break-all">{p.name}</p>
      <p className="text-sm font-black">{won(p.sale_price || p.price)}</p>
      <p className="text-[11px] text-gray-500">{p.sold_out ? '품절' : p.orderable ? '현재 판매가 기준 · 상품 보러가기' : '현재 주문 불가'}</p>
    </div>
  </Link>
);

export default function ReplayPage() {
  const { sessionId } = useParams();
  const [d, setD] = useState(undefined);
  const [src, setSrc] = useState(null);
  useEffect(() => {
    setD(undefined); setSrc(null);
    api.get(`/public/replays/${sessionId}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [sessionId]);
  const fetchUrl = useCallback(async () => abs((await api.get(`/public/replays/${sessionId}/play-url`)).data.url), [sessionId]);
  const play = async () => {
    try { setSrc(await fetchUrl()); } catch { setD(null); }
  };
  if (d === undefined) return <div className="min-h-screen p-6 text-sm text-gray-500">불러오는 중…</div>;
  if (!d) return <div className="min-h-screen p-6 text-sm" data-testid="replay-not-found">공개된 다시보기가 없습니다.</div>;
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] max-w-5xl mx-auto px-4 py-6 lg:grid lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-8" data-testid="replay-page">
      <div className="space-y-2">
        <span className="inline-flex items-center gap-1 text-[11px] font-black px-2 py-1 rounded bg-black text-white" data-testid="replay-badge"><History size={12} />지난 방송 다시보기 · 녹화 방송</span>
        {src ? <HlsPlayer src={src} testId="replay-player" refresh={fetchUrl} /> : (
          <button type="button" onClick={play} className="group relative block w-full max-w-[min(420px,39vh)] aspect-[9/16] mx-auto rounded-2xl overflow-hidden bg-black" data-testid="replay-play-btn">
            {d.thumbnail && <SafeImg src={d.thumbnail} alt="" className="w-full h-full object-cover opacity-60" />}
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
              <span className="w-16 h-16 rounded-full bg-white/90 text-black flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={26} className="fill-black ml-1" /></span>
              <span className="text-sm font-bold">다시보기 재생</span>
            </span>
          </button>
        )}
      </div>
      <div className="mt-4 lg:mt-0 space-y-4">
        <div>
          <h1 className="text-xl font-black break-all" data-testid="replay-title">{d.title}</h1>
          <p className="text-xs text-gray-500" data-testid="replay-meta">
            <Link to={`/seller/${d.seller_id}`} className="font-bold text-black dark:text-white">{d.seller_name}</Link> · {fmtKst(d.broadcast_started_at)} 방송{d.duration_sec ? ` · ${Math.floor(d.duration_sec / 60)}분 ${Math.round(d.duration_sec % 60)}초` : ''}
          </p>
          <p className="text-[11px] text-gray-400 mt-1">실시간 방송이 아닌 저장된 방송입니다. 가격·재고는 현재 판매 조건을 따릅니다.</p>
        </div>
        {d.products.length > 0 && (
          <section data-testid="replay-products">
            <h2 className="text-sm font-black mb-2">방송에서 소개한 상품</h2>
            <div className="grid gap-2">{d.products.map((p) => <ReplayProduct key={p.product_id} p={p} />)}</div>
          </section>
        )}
      </div>
    </div>
  );
}
